export const MIN_PASSWORD_LEN = 8;
export const KDF_ITERATIONS = 12000;
export const ENCRYPTION_LABEL = 'e2ee-v1';

function validatePassword(password) {
  const p = password != null ? String(password) : '';
  if (p.length < MIN_PASSWORD_LEN) {
    return { ok: false, message: `备份口令至少 ${MIN_PASSWORD_LEN} 位` };
  }
  return { ok: true, password: p };
}

function bytesToBase64(bytes) {
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

function base64ToBytes(base64) {
  const binary = atob(String(base64 || ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function deriveKey(password, saltBytes, iterations) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptPlaintext(plaintextUtf8, password) {
  const chk = validatePassword(password);
  if (!chk.ok) return chk;
  if (!crypto?.subtle) return { ok: false, message: '当前浏览器不支持 Web Crypto' };

  const saltBytes = crypto.getRandomValues(new Uint8Array(16));
  const ivBytes = crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveKey(chk.password, saltBytes, KDF_ITERATIONS);
  const enc = new TextEncoder();
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: ivBytes },
    key,
    enc.encode(String(plaintextUtf8 || ''))
  );

  return {
    ok: true,
    envelope: {
      encryption: ENCRYPTION_LABEL,
      kdf: 'pbkdf2-sha256',
      kdfIterations: KDF_ITERATIONS,
      cipher: 'aes-256-cbc',
      salt: bytesToBase64(saltBytes),
      iv: bytesToBase64(ivBytes),
      ciphertext: bytesToBase64(new Uint8Array(encrypted))
    }
  };
}

export async function decryptToPlaintext(envelope, password) {
  const chk = validatePassword(password);
  if (!chk.ok) return chk;
  if (!envelope || envelope.encryption !== ENCRYPTION_LABEL) {
    return { ok: false, message: '不支持的备份加密格式' };
  }
  try {
    const saltBytes = base64ToBytes(envelope.salt);
    const ivBytes = base64ToBytes(envelope.iv);
    const cipherBytes = base64ToBytes(envelope.ciphertext);
    const iterations = Number(envelope.kdfIterations) || KDF_ITERATIONS;
    const key = await deriveKey(chk.password, saltBytes, iterations);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv: ivBytes },
      key,
      cipherBytes
    );
    const text = new TextDecoder().decode(decrypted);
    if (!text) return { ok: false, message: '口令错误或备份已损坏' };
    return { ok: true, plaintext: text };
  } catch (error) {
    return { ok: false, message: '口令错误或备份已损坏' };
  }
}

export { validatePassword };
